import { EmailAccountBannedJob } from '@repo/jobs-api'
import {
  type FeatureFlagKey,
  getFeatureFlag,
  hasAnyPermission,
  hasPermission as hasPermissionFn,
  isAtLeastRole as isAtLeastRoleFn,
  type Permission,
  type UserRole,
} from '@repo/shared-common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { authClient } from '@web/src/lib/auth/auth-client'
import { cleanUpPushOnSignOut } from '@web/src/lib/pwa/push-subscription'
import { AuthCurrentUserContext, SessionUser } from '@web/src/lib/yoga/yoga-auth-context'
import { authUserSession } from '../lib/auth/auth-server'

export const authQueryKeys = {
  session: ['session'],
}

export const tiggerBanNotification = createServerFn({ method: 'POST' })
  .validator((data: { userId: string; endDate: Date }) => data)
  .handler(async ({ data }) => {
    await EmailAccountBannedJob.triggerJob({
      userId: data.userId,
      endDate: data.endDate,
    })
  })

export const useAuth = () => {
  const queryClient = useQueryClient()
  const router = useRouter()

  // Fetch session using TanStack Query for better caching and prefetching
  const session = useQuery({
    queryKey: authQueryKeys.session,
    queryFn: async () => {
      const result = await authClient.getSession({ query: {} })
      return result.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - matches server-side cookie cache
    gcTime: 10 * 60 * 1000, // 10 minutes
  })

  const context: AuthCurrentUserContext = {
    currentUser: session.data?.user as unknown as SessionUser,
    currentSession: session.data?.session,
  }

  const currentUserRole = context.currentUser?.roles

  /**
   * Check if the current user has a specific permission
   */
  const hasPermission = (permission: Permission): boolean => hasPermissionFn(currentUserRole, permission)

  /**
   * Check if the current user has any of the specified permissions
   */
  const hasAnyPerm = (permissions: Permission[]): boolean => hasAnyPermission(currentUserRole, permissions)

  /**
   * Check if the current user is at least the specified role level
   */
  const isAtLeastRole = (role: UserRole): boolean => isAtLeastRoleFn(currentUserRole, role)

  /**
   * Get a feature flag value for the current user's role
   */
  const getFeature = <K extends FeatureFlagKey>(key: K) => getFeatureFlag(currentUserRole, key)

  // Refresh the session from the database, bypassing the cookie cache
  const refreshSession = async () => {
    // An in-flight refetch would be deduped into query() and can resolve with cookie-cached (stale) data
    await queryClient.cancelQueries({ queryKey: authQueryKeys.session })

    await queryClient.query({
      queryKey: authQueryKeys.session,
      queryFn: () => authUserSession({ data: { disableCookieCache: true } }),
      staleTime: 0,
    })
  }

  // Login mutations
  const loginWithCredentials = useMutation({
    mutationFn: async ({
      email,
      password,
      captchaToken,
    }: {
      email: string
      password: string
      captchaToken: string
    }) => {
      const result = await authClient.signIn.email({
        email,
        password,
        fetchOptions: {
          headers: {
            'x-captcha-response': captchaToken,
          },
        },
      })

      if (result.error) {
        throw result.error || new Error('Authentication failed')
      }

      return result
    },
    onSuccess(response) {
      if (response.data?.user.id) {
        // Invalidate session cache to trigger refetch
        queryClient.invalidateQueries({ queryKey: authQueryKeys.session })
        router.navigate({ to: '/' })
      }
    },
    onError(error: Error) {
      console.error('Login error:', error)
    },
  })

  // Logout mutation (with navigation)
  const logOut = useMutation({
    mutationFn: async () => {
      await cleanUpPushOnSignOut()
      const result = await authClient.signOut({})
      if (result.error) {
        throw result.error || new Error('Failed to sign out')
      }
      return result
    },
    onSettled: async () => {
      queryClient.removeQueries({ queryKey: authQueryKeys.session })
      await router.navigate({ to: '/auth/login' })
    },
  })

  // Auth helper mutations
  const forgotPassword = useMutation({
    mutationFn: async ({ email, captchaToken }: { email: string; captchaToken: string }) => {
      const result = await authClient.requestPasswordReset({
        email,
        redirectTo: '/auth/reset-password',
        fetchOptions: {
          headers: {
            'x-captcha-response': captchaToken,
          },
        },
      })
      if (result.error) {
        throw result.error || new Error('Failed to request password reset')
      }
      return result
    },
  })

  const resetPassword = useMutation({
    mutationFn: async ({ newPassword, token }: { newPassword: string; token: string }) => {
      const result = await authClient.resetPassword({ newPassword, token })
      if (result.error) {
        throw result.error || new Error('Failed to reset password')
      }
      return result
    },
  })

  const changePassword = useMutation({
    mutationFn: async ({ newPassword, currentPassword }: { newPassword: string; currentPassword: string }) => {
      const result = await authClient.changePassword({ newPassword, currentPassword, revokeOtherSessions: true })
      if (result.error) {
        throw result.error || new Error('Failed to change password')
      }
      return result
    },
  })

  const sendVerificationEmail = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const result = await authClient.sendVerificationEmail({ email })
      if (result.error) {
        throw result.error || new Error('Failed to send verification email')
      }
      return result
    },
  })

  const verifyEmail = useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      const result = await authClient.verifyEmail({ query: { token } })
      if (result.error) {
        throw result.error || new Error('Failed to verify email')
      }
      return result
    },
  })

  const revokeSession = useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      const result = await authClient.revokeSession({ token })
      if (result.error) {
        throw result.error || new Error('Failed to revoke session')
      }
      return result
    },
  })

  const banUser = useMutation({
    mutationFn: async ({
      userId,
      banReason,
      banExpiresIn,
    }: {
      userId: string
      banReason: string
      banExpiresIn: number
    }) => {
      const result = await authClient.admin.banUser({ userId, banReason, banExpiresIn })
      if (result.error) {
        throw result.error || new Error('Failed to ban user')
      }

      const banEndDate = banExpiresIn ? new Date(Date.now() + banExpiresIn * 1000) : null

      await tiggerBanNotification({
        data: { userId, endDate: banEndDate },
      })

      return result
    },
  })

  const unbanUser = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const result = await authClient.admin.unbanUser({ userId })
      if (result.error) {
        throw result.error || new Error('Failed to unban user')
      }
      return result
    },
  })

  const impersonateUser = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const result = await authClient.admin.impersonateUser({ userId })
      if (result.error) {
        throw result.error || new Error('Failed to impersonate user')
      }
      return result
    },
  })

  const stopImpersonating = useMutation({
    mutationFn: async () => {
      const result = await authClient.admin.stopImpersonating({ query: {} })
      if (result.error) {
        throw result.error || new Error('Failed to stop impersonating')
      }
      return result
    },
  })

  const signOut = useMutation({
    mutationFn: async () => {
      await cleanUpPushOnSignOut()
      const result = await authClient.signOut({})
      if (result.error) {
        throw result.error || new Error('Failed to sign out')
      }
      return result
    },
  })

  const result = {
    error: session.error,
    isPending: session.isPending,
    isError: session.isError,
    isSuccess: session.isSuccess,
    loading: session.isPending,
    isAuthenticated: !!session.data?.user,
    isImpersonating: Boolean(session.data?.session?.impersonatedBy),
    ...context,
    // Permission-based functions
    hasPermission,
    hasAnyPerm,
    isAtLeastRole,
    getFeature,
    currentUserRole,
    refreshSession,
    // Login mutations
    loginWithCredentials,
    // Logout mutations
    logOut,
    signOut,
    // Ban user mutations
    banUser,
    unbanUser,
    // Impersonation mutations
    impersonateUser,
    stopImpersonating,
    // Auth helper mutations
    forgotPassword,
    resetPassword,
    changePassword,
    sendVerificationEmail,
    verifyEmail,
    revokeSession,
  }

  return result
}
